import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  ArrowPathIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CheckIcon,
  CloudIcon,
  CpuChipIcon,
  DocumentTextIcon,
  EyeIcon,
  ShieldCheckIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import ServiceLayout from '../../components/ServiceLayout';
import ServiceTabs from '../../components/ServiceTabs';
import {
  Block,
  Card,
  CardTitle,
  Chip,
  ChipList,
  FaqCall,
  SectionHead,
  SectionTitle,
  StepList,
} from '../../components/ServiceBlocks';
import { JobPanel, OfferCards, SafetyBand } from '../../components/AIServiceBlocks';
import { AI_TRIAGED, HOW_WE_MEASURE_ANCHORS } from '../../data/proofPoints';
import SEO from '../../components/SEO';
import { bookingClickHandler } from '../../utils/bookingLink';
import { faqSlug } from '../../utils/faqSlug';
import { trackContactClick, trackEvent } from '../../utils/analytics';
import { useScrollDepth } from '../../hooks/useScrollDepth';

/*
 * Round 30: /services/ai-solutions is the AI money page. One page (not two) for AI agents, AI
 * automation (the agency offer) and AI strategy, in the banded pattern rounds 27 and 29 built
 * for managed IT: a light hero, one dark band, one tab card, disclosures, takeaway H2s, one
 * primary action and a phone-only sticky bar. The design, copy and claims review are in
 * .studio/rounds/30; the page budget is docs/WRITING-CONTRACT.md (about 650 visible words at
 * 390px, and the static ceiling in scripts/page-ceilings.json, which this page must not raise).
 *
 * Target phrases (Keyword Planner, Canada): "ai agency", "ai agents", "ai automation", "ai
 * automation agency", "ai consulting", "ai implementation". Keep <SEO> in step with PAGE_META in
 * app.py: the parity gate in tests/test_route_metadata_graph.py fails the build otherwise.
 *
 * Claims rule, kept exactly:
 *  - the only measured figure is AI_TRIAGED (proofPoints.ts), shown twice with its period and
 *    always badged "In-house platform", because it is our own help desk and not client proof;
 *  - every project link carries a badge derived from the catalogue (`engagement`, and
 *    `outcomeStatus` for the commissioned-only project), see AIServiceBlocks `sourceKindOf`;
 *  - no price, no outcome number, no timeline. Discovery is "a fixed fee, agreed in writing",
 *    the first call is free and the client owns what we build (owner answers, 2026-10-22);
 *  - the AI work is done remotely for organisations across Canada, from Calgary.
 *
 * GA4 (the three registered event-scoped dimensions only, no personal data): booking_click and
 * contact_click with cta_location; ai_solutions_assessment_click with cta_location;
 * ai_solutions_tab with tab_name; ai_solutions_faq_open with faq_question;
 * ai_solutions_project_click with cta_location; ai_solutions_scroll via useScrollDepth.
 *
 * No FAQ schema on purpose (SEO-PLAYBOOK): the FAQ is visible text only. The tab panels, the FAQ
 * answers and the definition panel render into the HTML (hidden, never mounted on click).
 * The page is frozen for 6 to 8 weeks after this round: change it only with reading data.
 */

/** The period in a proofPoints.ts label, e.g. "Jul–Oct 2026" from "Helpdesk tickets AI-triaged (Jul–Oct 2026)". */
const periodOf = (label: string): string => label.match(/\(([^)]+)\)/)?.[1] ?? '';
const AI_TRIAGED_PERIOD = periodOf(AI_TRIAGED.label);

/** Where on the page a booking, phone or assessment click came from. */
const at = (ctaLocation: string) => ({ cta_location: ctaLocation });

const trackAssessmentClick = (ctaLocation: string): void => {
  trackEvent('ai_solutions_assessment_click', at(ctaLocation));
};

const trackProjectClick = (ctaLocation: string): (() => void) => () => {
  trackEvent('ai_solutions_project_click', at(ctaLocation));
};

/* ------------------------------------------------------------------- copy */

const faqItems = [
  {
    question: 'What is the difference between an AI agent and AI automation?',
    answer:
      'Automation follows fixed steps. An agent reads something unstructured, such as an email, a ticket or an invoice, and decides what to do within limits you set. Many jobs need both, and some need only plain automation.',
  },
  {
    question: 'What does an AI agent or automation cost?',
    answer:
      'It depends on the scope. The 30-minute call is free, and discovery is a fixed fee agreed in writing. We then agree a first milestone, the scope and the price in writing before building starts.',
  },
  {
    question: 'Will AI replace our employees?',
    answer:
      'No. We give your people tools they direct. Your accountant reviews what an AI drafts and decides what goes ahead. The person stays accountable.',
  },
  {
    question: 'What if the AI makes a mistake?',
    answer:
      'It will, so we design for it. By default a person checks anything that touches customers, money or records, and we fix the cause, not just the output.',
  },
  {
    question: 'Where does our data go?',
    answer:
      'Where your data lives is agreed before we build anything. We can deploy in your own cloud account or on your servers, and use your security protocols.',
  },
  {
    question: 'Can we start small?',
    answer:
      'That is how we prefer to start: one problem, an agreed scope, and you decide whether to expand once you have seen it work.',
  },
  {
    question: 'We tried AI before and it did not work. What is different?',
    answer:
      'AI projects often fail because the scope was too big or nobody owned the result. We start small, check the result with you, then decide the next step. Sometimes we tell you a goal needs building in steps.',
  },
  {
    question: 'Are you tied to one AI vendor?',
    answer:
      'No. The agents we run use models from several providers, and we can run two side by side to compare. We pick the model for the job, and can use your own API accounts.',
  },
  {
    question: 'Do you work with businesses outside Calgary?',
    answer: (
      <>
        Yes. We are based in Calgary and do AI work remotely for organisations across Canada. Phone us on{' '}
        <a href="tel:+14032459429" onClick={() => trackContactClick('phone', 'ai_solutions_faq', at('faq'))}>
          (403) 245-9429
        </a>
        .
      </>
    ),
  },
];

/* ------------------------------------------------------------------- layout */

/* A block of this page: balanced heading lines, so a long takeaway H2 never leaves a one-word last line. */
const AiBlock = styled(Block)`
  h2,
  h3 {
    text-wrap: balance;
  }
`;

const HeroProof = styled.span`
  b {
    color: var(--davinci-midnight);
  }

  a {
    color: var(--davinci-midnight);
    font-weight: var(--font-weight-semibold);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  a:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

const MoreProjects = styled(Link)`
  display: inline-block;
  margin-top: 14px;
  font-weight: var(--font-weight-bold);
  color: var(--color-accent-primary-fill);
  text-decoration: underline;
  text-underline-offset: 3px;

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const TwoUp = styled.div`
  display: grid;
  gap: 16px;
  align-items: start;
`;

const StartGrid = styled(TwoUp)`
  @media (min-width: 900px) {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    gap: 22px;
  }
`;

const ReadyCard = styled.div`
  padding: 20px 18px 22px;
  border: 1px solid rgba(11, 152, 206, 0.28);
  border-radius: 20px;
  background: linear-gradient(180deg, var(--white), rgba(11, 152, 206, 0.05));
  box-shadow: 0 6px 20px rgba(20, 53, 98, 0.08);
  color: var(--text-primary);

  @media (min-width: 768px) {
    padding: 26px 28px 28px;
  }

  p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  .fine {
    margin-top: 0.6rem;
    font-size: max(13px, 0.82rem);
    line-height: 1.4;
  }
`;

const ReadyButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  min-height: 48px;
  margin-top: 16px;
  padding: 0.5rem 1.2rem;
  border: 2px solid var(--color-accent-primary-fill);
  border-radius: 12px;
  background: var(--white);
  color: var(--color-accent-primary-fill);
  font-weight: var(--font-weight-bold);
  line-height: 1.25;
  text-align: center;
  text-decoration: none;
  transition: background-color var(--transition-fast);

  &:hover {
    background: var(--davinci-steam);
  }

  &:focus-visible {
    outline: 3px solid var(--davinci-blue-fill);
    outline-offset: 2px;
  }
`;

const ScopeGrid = styled(TwoUp)`
  gap: 18px;

  @media (min-width: 960px) {
    grid-template-columns: minmax(0, 6fr) minmax(0, 5fr);
    gap: 44px;
    align-items: center;
  }
`;

const ScopeCopy = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  line-height: var(--lh-normal);
  color: var(--text-primary);
`;

/* ------------------------------------------------------------------- page */

const AIServices: React.FC = () => {
  useScrollDepth('ai_solutions_scroll');

  return (
    <>
    <SEO title="AI Agency: AI Agents, Automation & Strategy | Davinci" description="Calgary AI agency serving Canada: we build and run AI agents and AI automation in your systems, and show where AI pays off first. Free AI Readiness Score." path="/services/ai-solutions" />
    <ServiceLayout
      layout="banded"
      serviceName="AI agency · Calgary-based, serving Canada"
      icon={<SparklesIcon />}
      hideBackLink
      title="AI agents and AI automation, built and run"
      subtitle="We find where AI pays off, build agents and automations inside your systems, and run them with a person in charge."
      heroPrice={{
        line: 'Scoped one workflow at a time, with a fixed-fee discovery agreed in writing.',
        balance: true,
        note: (
          <HeroProof>
            We run AI on our own help desk: <b>{AI_TRIAGED.value}</b> of client tickets AI-triaged, {AI_TRIAGED_PERIOD}.{' '}
            <Link to={HOW_WE_MEASURE_ANCHORS.aiTriage}>How we measure</Link>
          </HeroProof>
        ),
      }}
      heroCta={{
        buttonText: 'Book a free 30-minute AI call',
        href: '/book',
        reloadDocument: true,
        onClick: bookingClickHandler('ai_solutions_hero', at('hero')),
      }}
      heroSecondary={{ text: 'Get your free AI Readiness Score', href: '/assessment', onClick: () => trackAssessmentClick('hero') }}
      stickyCta={{
        cta: {
          buttonText: 'Book a free 30-minute AI call',
          href: '/book',
          reloadDocument: true,
          onClick: bookingClickHandler('ai_solutions_sticky', at('sticky_bar')),
        },
        phone: {
          href: 'tel:+14032459429',
          label: 'Call (403) 245-9429',
          onClick: () => trackContactClick('phone', 'ai_solutions_sticky', at('sticky_bar')),
        },
      }}
      cta={{
        title: 'Ready to see where AI would pay off in your business?',
        description: 'In 30 minutes we will look at one workflow and what a first step would involve.',
        buttonText: 'Book a free 30-minute AI call',
        href: '/book',
        reloadDocument: true,
        onClick: bookingClickHandler('ai_solutions_page', at('closing')),
        secondary: { text: 'Get your free AI Readiness Score', href: '/assessment', onClick: () => trackAssessmentClick('closing') },
        footer: (
          <>
            or call{' '}
            <a href="tel:+14032459429" onClick={() => trackContactClick('phone', 'ai_solutions', at('closing'))}>(403) 245-9429</a>
          </>
        ),
      }}
      faqs={{
        heading: 'AI agent and automation questions, answered',
        eyebrow: 'Questions',
        items: faqItems,
        // The last three are side questions; they stay in the HTML behind "More questions".
        initialVisible: 6,
        onOpen: (item) => trackEvent('ai_solutions_faq_open', { faq_question: faqSlug(item.question) }),
        aside: (
          <FaqCall>
            Something else? Call{' '}
            <a href="tel:+14032459429" onClick={() => trackContactClick('phone', 'ai_solutions_faq', at('faq'))}>(403) 245-9429</a>.
          </FaqCall>
        ),
      }}
    >
      <AiBlock aria-labelledby="ai-offers">
        <SectionHead id="ai-offers" title="AI agents, AI automation and AI strategy from one Calgary team" />
        <OfferCards
          offers={[
            {
              icon: <CpuChipIcon />,
              title: 'AI agents that act in your systems',
              text: 'Software that reads your email, tickets or records, then sorts, drafts and matches. It hands over to a person when it is unsure.',
              first: 'one narrow task, checked by your people.',
            },
            {
              icon: <ArrowPathIcon />,
              title: 'AI automation, built and run',
              text: 'We build the workflow, connect it to the tools you already use, then monitor and maintain it.',
              first: 'one repetitive job your team does every week.',
            },
            {
              icon: <ChartBarIcon />,
              title: 'AI strategy: where AI pays off first',
              text: 'We screen your workflows for opportunity and readiness, then rank them by value and risk, so you fund the right one first.',
              first: (
                <>
                  <Link to="/assessment" onClick={() => trackAssessmentClick('offers')}>the free AI Readiness Score</Link>.
                </>
              ),
            },
          ]}
        />
      </AiBlock>

      <AiBlock aria-labelledby="ai-first">
        <SectionHead id="ai-first" title="Where AI pays off first: four jobs we have already built" />
        <ServiceTabs
          label="AI jobs we have built"
          onSelect={(key) => trackEvent('ai_solutions_tab', { tab_name: key })}
          tabs={[
            {
              key: 'paperwork',
              label: 'Paperwork',
              icon: <DocumentTextIcon aria-hidden="true" />,
              panel: (
                <JobPanel
                  title="Invoices and quote requests, read and matched"
                  links={[{ slug: 'ai-quoting-engine-for-industrial-distribution', label: 'Quote requests matched to parts' }]}
                  art={{ slug: 'ai-accounts-payable-categorisation', caption: 'Supplier invoices into the ledger' }}
                  onProjectClick={trackProjectClick('tab_paperwork')}
                >
                  AI reads supplier invoices from a mailbox and matches them to the ledger. A second system reads quote requests and matches them to a catalogue of nearly 12,000 parts; a person confirms.
                </JobPanel>
              ),
            },
            {
              key: 'support',
              label: 'Support',
              icon: <ChatBubbleLeftRightIcon aria-hidden="true" />,
              panel: (
                <JobPanel
                  title="Tickets sorted, first-line answers drafted"
                  links={[{ slug: 'ticket-classification-and-routing', label: 'Every ticket classified on arrival' }]}
                  art={{ slug: 'tier-zero-support-agent', caption: 'A support agent that knows when to hand over' }}
                  onProjectClick={trackProjectClick('tab_support')}
                >
                  Our own agents classify every ticket on arrival, draft first-line answers from our documentation for a technician to review, and hand over when confidence is low. The AI never replies to a customer.
                </JobPanel>
              ),
            },
            {
              key: 'reporting',
              label: 'Reporting',
              icon: <ChartBarIcon aria-hidden="true" />,
              panel: (
                <JobPanel
                  title="Weekly updates written, business data you can ask"
                  links={[{ slug: 'model-context-protocol-erp-integration', label: 'Accounting data for AI assistants' }]}
                  art={{ slug: 'leo-ai-project-reporting-agent', caption: 'Leo, the agent that writes the weekly update' }}
                  onProjectClick={trackProjectClick('tab_reporting')}
                >
                  Our agent Leo drafts the weekly client update from live project data, and a person reviews it before it is sent. A client also commissioned us to connect their accounting system to AI assistants through an open standard.
                </JobPanel>
              ),
            },
            {
              key: 'staff_ai',
              label: 'Staff AI',
              icon: <SparklesIcon aria-hidden="true" />,
              panel: (
                <JobPanel
                  title="A company AI assistant that stays inside your cloud"
                  art={{ slug: 'private-ai-chat-platform', caption: 'A private AI assistant for staff' }}
                  onProjectClick={trackProjectClick('tab_staff_ai')}
                >
                  We deployed a private AI chat platform in a client&apos;s own cloud tenant, behind their own sign-in. Access follows the staff directory, and several model providers sit behind one screen.
                </JobPanel>
              ),
            },
          ]}
        />
        <MoreProjects to="/projects?service=AI+%26+Automation" onClick={trackProjectClick('all_projects')}>
          All AI projects &rarr;
        </MoreProjects>
      </AiBlock>

      <SafetyBand
        id="ai-safe"
        heading="AI agents you can check, with a person in charge"
        stat={{
          value: AI_TRIAGED.value,
          text: `of client help-desk tickets AI-triaged on our own platform, ${AI_TRIAGED_PERIOD}.`,
          note: 'The AI sorts and briefs. It never replies to a customer.',
          slug: 'ticket-classification-and-routing',
        }}
        rules={[
          { icon: <ShieldCheckIcon />, title: 'A person decides', text: 'By default, a person checks anything that touches customers, money or records.' },
          { icon: <EyeIcon />, title: 'Shadow mode first', text: 'New agents draft and log without sending until we have reviewed their work.' },
          { icon: <ChartBarIcon />, title: 'Scored, not asserted', text: 'We test accuracy on a held-out set, so changes are judged, not guessed.' },
          { icon: <CloudIcon />, title: 'Your data, your tenant', text: 'Deploy in your own cloud account or on your servers, behind your sign-in.' },
        ]}
        disclosureLabel="How we measure this"
      >
        <strong>{AI_TRIAGED.value}.</strong> The share of client tickets whose category and priority were set by our AI triage rather than by a person, 20 July to 19 October 2026. A technician writes every customer reply. Source: Compass, the platform we built and run.{' '}
        <Link to={HOW_WE_MEASURE_ANCHORS.aiTriage}>Full definition: how we measure</Link>
      </SafetyBand>

      <AiBlock aria-labelledby="ai-start">
        <SectionHead id="ai-start" title="AI strategy and implementation in four steps, one workflow first" />
        <StartGrid>
          <Card>
            <StepList>
              <li><strong>Score or call</strong>Screen one workflow with the free AI Readiness Score, or book a call.</li>
              <li><strong>Discover</strong>We map the workflow, the systems and the data, then scope the work.</li>
              <li><strong>Pilot</strong>One narrow agent or automation, reviewed by your people before it acts alone.</li>
              <li><strong>Run</strong>We monitor, fix and extend it, and check it against what we agreed.</li>
            </StepList>
          </Card>
          <ReadyCard>
            <CardTitle>Free AI Readiness Score</CardTitle>
            <p>
              You get a grade, separate opportunity and execution scores, and a route: AI-assisted, hybrid, deterministic automation or human-led.
            </p>
            <ChipList>
              <Chip icon={<CheckIcon aria-hidden="true" />}>Free</Chip>
              <Chip icon={<CheckIcon aria-hidden="true" />}>7–10 minutes</Chip>
              <Chip icon={<CheckIcon aria-hidden="true" />}>One workflow</Chip>
            </ChipList>
            <ReadyButton to="/assessment" onClick={() => trackAssessmentClick('steps')}>Start the Quick Score</ReadyButton>
            <p className="fine">You see the result before you share any contact details.</p>
          </ReadyCard>
        </StartGrid>
      </AiBlock>

      <AiBlock aria-labelledby="ai-price">
        <ScopeGrid>
          <div>
            <SectionTitle id="ai-price">AI agent pricing: scoped after a short discovery</SectionTitle>
            <ScopeCopy>
              AI work varies too much for one number on a page. The 30-minute call is free. Discovery is a fixed fee, agreed in writing, and ends with a first milestone and a price before building starts. You own what we build for you.
            </ScopeCopy>
          </div>
          <Card>
            <CardTitle>What shapes the scope</CardTitle>
            <ChipList>
              <Chip>Systems we connect to</Chip>
              <Chip>State of your data</Chip>
              <Chip>Volume</Chip>
              <Chip>Who approves what</Chip>
              <Chip>Where it runs</Chip>
            </ChipList>
          </Card>
        </ScopeGrid>
      </AiBlock>
    </ServiceLayout>
    </>
  );
};

export default AIServices;
